/**
 * DiceAssetManager — Loads, caches, and pools custom dice models.
 *
 * Three-tier cache:
 *   L1: documentCache  — DiceAssetBundle (document + model info) per dice ID
 *   L2: templateCache  — Parsed GLB template (THREE.Group) per model code
 *   L3: pool           — Reusable cloned meshes per model code
 *
 * Inspired by PhoneDice DiceManager: load GLB once, clone from template,
 * apply materials per-instance, pool released dice for reuse.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { DiceAssetBundle, MaterialSlotConfig, DiceEnvironment } from '@sdl/shared';
import { ENV_PRESET_EXT, ENV_CUBE_FACES, DEFAULT_DICE_ID } from '@sdl/shared';
import { buildMaterial, disposeMaterial, clearTextureCache } from './materialBuilder';
import { prewarmShaders } from '../../../engine/render/precompile';
import { renderScheduler } from '../../../engine/render/RenderScheduler';

// ── Types ───────────────────────────────────────────────────────

export interface DiceAssetManagerConfig {
  cdnBase: string;
  apiBaseUrl: string;
}

/** Returned by acquireDice — everything needed to create a Dice entity */
export interface DiceAsset {
  diceId: string;
  modelCode: string;
  mesh: THREE.Group;
  halfExtents: [number, number, number];
  faceMap: number[];
  shaderMaterials: THREE.ShaderMaterial[];
  /** Cached physics body — created on first use, reused across pool cycles */
  body: import('cannon-es').Body | null;
  /** Set by anything that mutates this asset's materials at runtime (e.g.
   *  Dice.setGhosted). Forces the material rebuild on the next pool acquire
   *  even for the same skin — the same-skin fast path assumes pristine
   *  materials. */
  materialsMutated?: boolean;
}

/** Model metadata from /api/dice-models (cached at startup) */
export interface DiceModelInfo {
  code: string;
  faces: number[];
  glbUrl: string;
  physicsBox?: { halfExtents: [number, number, number] };
}

interface GlbTemplate {
  scene: THREE.Group;
  /** slotName → mesh names (for matching after clone) */
  slotMap: Map<string, string[]>;
  halfExtents: [number, number, number];
}

// ── Slot Detection ──────────────────────────────────────────────

function detectSlotName(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.includes('body')) return 'body';
  if (lower.includes('pip') || lower.includes('circle')) return 'pips';
  if (lower.includes('image') || lower.includes('decal')) return 'image';
  if (lower.includes('ring') || lower.includes('border')) return 'ring';
  if (lower.includes('face') && (lower.includes('1') || lower.includes('one'))) return 'face1';
  return null;
}

// ── DiceAssetManager ────────────────────────────────────────────

const MAX_POOL_SIZE_PER_MODEL = 6;
const gltfLoader = new GLTFLoader();

export class DiceAssetManager {
  private apiBaseUrl: string;

  // L1: DiceAssetBundle cache (document + model info) keyed by diceId
  private documentCache = new Map<string, DiceAssetBundle>();

  // L2: Parsed GLB template keyed by model code (e.g. "DSS01")
  private templateCache = new Map<string, GlbTemplate>();

  // L3: Pool of released DiceAssets keyed by model code
  private pool = new Map<string, DiceAsset[]>();

  // In-flight fetch dedup: prevents duplicate fetches for the same model
  private glbFetchPromises = new Map<string, Promise<GlbTemplate>>();
  private apiFetchPromises = new Map<string, Promise<void>>();

  // Track all active shader materials for time updates
  private activeShaderMaterials = new Set<THREE.ShaderMaterial>();

  // Per-die reflection environments. RAW CubeTextures keyed by preset name,
  // loaded once and shared across all dice that use them. We assign the cube
  // straight to material.envMap (Phone Dice parity) rather than PMREM-processing
  // it — PMREM's HalfFloat render targets blew the installed app's WKWebView GPU
  // budget. See loadEnvCubemap for the full rationale.
  private envCache = new Map<string, THREE.Texture>();
  private envFetchPromises = new Map<string, Promise<THREE.Texture | null>>();
  private cubeLoader = new THREE.CubeTextureLoader();

  /** Model metadata from /api/dice-models (faces, physicsBox, glbUrl per code) */
  private modelMetadata = new Map<string, DiceModelInfo>();

  constructor(config: DiceAssetManagerConfig) {
    this.apiBaseUrl = config.apiBaseUrl;
  }

  // ── Startup Preload ─────────────────────────────────────────

  /**
   * Preload ALL dice GLB models at app startup (PhoneDice DiceManager pattern).
   * Fetches the model catalog, downloads every GLB in parallel, and stores
   * templates in cache. After this, acquireDice only needs the document (skin)
   * — the heavy GLB is already parsed and ready to clone.
   */
  async preloadAllModels(): Promise<void> {
    try {
      const resp = await fetch(`${this.apiBaseUrl}/api/dice-models`);
      if (!resp.ok) {
        console.warn('[DiceAssetManager] Failed to fetch dice models catalog:', resp.status);
        return;
      }
      const models: DiceModelInfo[] = await resp.json();

      for (const model of models) {
        this.modelMetadata.set(model.code, model);
      }

      const uncached = models.filter((m) => !this.templateCache.has(m.code));
      if (uncached.length === 0) {
        return;
      }

      // Sequential, NOT Promise.all. Loading every model in parallel fires all
      // fetches at once and then parses + GPU-uploads them back-to-back with no
      // gaps — one long unbroken block that freezes the thread, starves the
      // socket heartbeat (→ disconnect), and slams the GPU all at once (which
      // compounds across refreshes). One at a time lets the thread breathe
      // during each fetch: the loader animates, the heartbeat fires, and
      // textures upload one-per-frame instead of in a single burst. We still
      // load them all before boot continues.
      for (const m of uncached) {
        try {
          await this.fetchGlb(m.code, m.glbUrl);
        } catch (err) {
          console.warn(`[DiceAssetManager] model ${m.code} preload failed:`, err);
        }
      }

    } catch (err) {
      console.warn('[DiceAssetManager] preloadAllModels error:', err);
    }
  }

  /** Get cached model metadata by code */
  getModelInfo(code: string): DiceModelInfo | undefined {
    return this.modelMetadata.get(code);
  }

  // ── Per-Dice Preload ────────────────────────────────────────

  /**
   * Preload assets for a list of dice IDs.
   * Fetches DiceDocuments from API, then GLBs from CDN.
   * Safe to call multiple times — deduplicates in-flight requests.
   */
  async preload(diceIds: string[]): Promise<void> {
    // Filter out already-cached IDs
    const uncached = diceIds.filter((id) => id && !this.documentCache.has(id));
    if (uncached.length === 0) return;

    // Deduplicate API fetch
    const cacheKey = uncached.sort().join(',');
    if (!this.apiFetchPromises.has(cacheKey)) {
      this.apiFetchPromises.set(cacheKey, this.fetchDocuments(uncached));
    }
    await this.apiFetchPromises.get(cacheKey);
    this.apiFetchPromises.delete(cacheKey);

    // Fetch GLBs for all unique models + reflection cubemaps for all env presets
    const modelCodes = new Set<string>();
    const envPresets = new Set<string>();
    for (const id of diceIds) {
      const bundle = this.documentCache.get(id);
      if (bundle && !this.templateCache.has(bundle.model.code)) {
        modelCodes.add(bundle.model.code);
      }
      const preset = bundle?.document.environment?.preset;
      if (preset) envPresets.add(preset);
    }

    await Promise.all([
      ...[...modelCodes].map((code) => this.fetchGlb(code)),
      ...[...envPresets].map((preset) => this.loadEnvCubemap(preset)),
    ]);
  }

  /** Fetch DiceAssetBundles from the batch API */
  private async fetchDocuments(diceIds: string[]): Promise<void> {
    try {
      const url = `${this.apiBaseUrl}/api/dice-assets?ids=${diceIds.join(',')}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn(`[DiceAssetManager] API fetch failed: ${resp.status}`);
        return;
      }
      const data = await resp.json();
      const assets: DiceAssetBundle[] = data.assets ?? [];
      for (const bundle of assets) {
        this.documentCache.set(bundle.diceId, bundle);
      }
    } catch (err) {
      console.warn('[DiceAssetManager] API fetch error:', err);
    }
  }

  /** Fetch and parse a GLB from CDN, store as template */
  private async fetchGlb(modelCode: string, directUrl?: string): Promise<GlbTemplate> {
    if (this.templateCache.has(modelCode)) {
      return this.templateCache.get(modelCode)!;
    }

    // Dedup in-flight GLB fetches
    if (this.glbFetchPromises.has(modelCode)) {
      return this.glbFetchPromises.get(modelCode)!;
    }

    const promise = this.loadGlbTemplate(modelCode, directUrl);
    this.glbFetchPromises.set(modelCode, promise);

    try {
      const template = await promise;
      this.templateCache.set(modelCode, template);
      return template;
    } finally {
      this.glbFetchPromises.delete(modelCode);
    }
  }

  /** Load GLB ArrayBuffer and parse into template */
  private async loadGlbTemplate(modelCode: string, directUrl?: string): Promise<GlbTemplate> {
    // Use direct URL (from preloadAllModels) or find from cached bundles
    let glbUrl = directUrl ?? '';
    if (!glbUrl) {
      for (const bundle of this.documentCache.values()) {
        if (bundle.model.code === modelCode) {
          glbUrl = bundle.model.glbUrl;
          break;
        }
      }
    }
    // Fall back to model metadata (from preloadAllModels catalog)
    if (!glbUrl) {
      const meta = this.modelMetadata.get(modelCode);
      if (meta) glbUrl = meta.glbUrl;
    }

    if (!glbUrl) {
      throw new Error(`[DiceAssetManager] No GLB URL for model ${modelCode}`);
    }

    // Fetch GLB as ArrayBuffer. The base models are SHIPPED with the client
    // (client/public/assets/dice-models/<code>.glb) — a fixed set that never
    // changes — so we read them locally for instant, offline, network-free
    // loading. Any model NOT in the bundle (e.g. one added after this build)
    // falls back to the CDN url, so the catalog stays flexible and the editor /
    // other apps keep using the CDN unchanged.
    const localUrl = `${import.meta.env.BASE_URL}assets/dice-models/${modelCode}.glb`;
    let arrayBuffer: ArrayBuffer;
    try {
      const localResp = await fetch(localUrl);
      if (!localResp.ok) throw new Error(`local ${localResp.status}`);
      arrayBuffer = await localResp.arrayBuffer();
    } catch {
      const resp = await fetch(glbUrl);
      if (!resp.ok) throw new Error(`GLB fetch failed: ${resp.status} for ${glbUrl}`);
      arrayBuffer = await resp.arrayBuffer();
    }

    return this.parseGlbTemplate(arrayBuffer);
  }

  /**
   * Parse a GLB ArrayBuffer into a reusable template: GLTF parse → slot
   * detection → centering → half-extents. Shared by the network path
   * (loadGlbTemplate) and the editor live-preview path (registerLiveDice) so
   * both build templates through identical logic.
   */
  private async parseGlbTemplate(arrayBuffer: ArrayBuffer): Promise<GlbTemplate> {
    const gltf = await new Promise<any>((resolve, reject) => {
      gltfLoader.parse(arrayBuffer, '', resolve, reject);
    });

    const scene: THREE.Group = gltf.scene;

    // Detect slot meshes by material/mesh name
    const slotMap = new Map<string, string[]>();
    const detectedNames = new Set<string>();

    scene.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const mesh = node as THREE.Mesh;
      const matName = (mesh.material as THREE.Material)?.name || '';
      const meshName = mesh.name || '';
      const slotName = detectSlotName(matName) || detectSlotName(meshName) || matName || meshName || null;

      if (slotName) {
        if (!slotMap.has(slotName)) slotMap.set(slotName, []);
        slotMap.get(slotName)!.push(mesh.name || mesh.uuid);
        detectedNames.add(slotName);
      }
    });

    // Fallback: treat entire model as "body"
    if (detectedNames.size === 0) {
      scene.traverse((node: THREE.Object3D) => {
        if ((node as THREE.Mesh).isMesh) {
          if (!slotMap.has('body')) slotMap.set('body', []);
          slotMap.get('body')!.push(node.name || node.uuid);
          detectedNames.add('body');
        }
      });
    }

    // Center the model
    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    scene.position.sub(center);

    // Compute half-extents after centering
    const centeredBox = new THREE.Box3().setFromObject(scene);
    const size = centeredBox.getSize(new THREE.Vector3());
    const halfExtents: [number, number, number] = [
      parseFloat((size.x / 2).toFixed(4)),
      parseFloat((size.y / 2).toFixed(4)),
      parseFloat((size.z / 2).toFixed(4)),
    ];

    return { scene, slotMap, halfExtents };
  }

  // ── Live (editor) injection ─────────────────────────────────

  /**
   * Register an in-memory, UNSAVED dice for preview (dice-editor only).
   *
   * Parses the supplied GLB bytes into a template and stores a synthetic
   * DiceAssetBundle so that, afterwards, `isReady(diceId)` is true and
   * `acquireDice(diceId)` clones the model + applies the supplied DiceDocument
   * slots through the EXACT same pool / material / shader path the live game
   * uses — with no server or CDN round-trip. This is what lets the dice editor
   * shoot the dice it is currently editing before it has ever been saved.
   *
   * Re-parses the GLB only when `modelCode` is new; on a material-only edit the
   * caller passes the same modelCode so only the bundle is (re)registered.
   * NOTE: the caller must use a FRESH diceId per re-skin — acquireDice's
   * same-skin fast path skips the material rebuild on pool hits when the
   * diceId is unchanged, so re-registering under the same id shows stale
   * materials (DicePlaybackEngine bumps a skinRev suffix for this).
   *
   * @param glb         Raw .glb bytes (re-parsed only when modelCode is unseen)
   * @param slots       Current (possibly unsaved) DiceDocument.slots
   * @param opts.faces  Face value map for result reading (visual preview: defaults to 1..6)
   * @param opts.halfExtents  Physics box half-extents (defaults to the GLB's computed bounds)
   * @param opts.environment  Per-die reflection environment (preset cubemap loaded here)
   */
  async registerLiveDice(
    diceId: string,
    modelCode: string,
    glb: ArrayBuffer,
    slots: Record<string, MaterialSlotConfig>,
    opts?: { faces?: number[]; halfExtents?: [number, number, number]; environment?: DiceEnvironment },
  ): Promise<void> {
    if (!this.templateCache.has(modelCode)) {
      const template = await this.parseGlbTemplate(glb);
      this.templateCache.set(modelCode, template);
    }

    // Preload the reflection cubemap so acquireDice (sync) can assign it.
    if (opts?.environment?.preset) {
      await this.loadEnvCubemap(opts.environment.preset);
    }

    const faces = opts?.faces ?? [1, 2, 3, 4, 5, 6];

    this.documentCache.set(diceId, {
      diceId,
      document: { slots, environment: opts?.environment } as any,
      model: {
        code: modelCode,
        faces,
        glbUrl: '',
        physicsBox: opts?.halfExtents ? { halfExtents: opts.halfExtents } : undefined,
      },
    } as DiceAssetBundle);
  }

  // ── Reflection Environment ──────────────────────────────────

  /**
   * Load (and cache) a reflection env preset as a RAW CubeTexture assigned
   * straight to material.envMap — mirrors Phone Dice, which runs the same per-die
   * env maps on iOS WKWebView with zero memory issues. Each die carries its own
   * environment (the table scene provides none). Served from
   * `${BASE_URL}assets/env/<preset>/`. Resolves null (not throws) on failure.
   *
   * Deliberately NOT PMREM-processed: a PMREM HalfFloat render target + mip chain
   * costs ~4–8× the VRAM of this 8-bit cube PER preset, and that overhead (across
   * several live presets plus the login/bonus PMREM generators) is what pushed the
   * installed app's tighter WKWebView GPU budget into Jetsam → WebGL context loss
   * → black render. Glossy dice (low roughness) reflect a raw cube sharply; very
   * matte skins reflect less — bump envMapIntensity / lower roughness if any look flat.
   */
  async loadEnvCubemap(preset: string | undefined): Promise<THREE.Texture | null> {
    if (!preset) return null;
    if (this.envCache.has(preset)) return this.envCache.get(preset)!;
    if (this.envFetchPromises.has(preset)) return this.envFetchPromises.get(preset)!;

    const ext = ENV_PRESET_EXT[preset] ?? 'png';
    const urls = ENV_CUBE_FACES.map(
      (f) => `${import.meta.env.BASE_URL}assets/env/${preset}/${f}.${ext}`,
    );

    const promise = new Promise<THREE.Texture | null>((resolve) => {
      this.cubeLoader.load(
        urls,
        (cube) => {
          // Raw 8-bit cube straight onto the material (Phone Dice parity). No PMREM.
          cube.colorSpace = THREE.SRGBColorSpace;
          cube.format = THREE.RGBAFormat;
          cube.anisotropy = 0;
          this.envCache.set(preset, cube);
          resolve(cube);
        },
        undefined,
        (err) => {
          console.warn(`[DiceAssetManager] env preset "${preset}" failed to load:`, err);
          resolve(null);
        },
      );
    });
    this.envFetchPromises.set(preset, promise);
    const result = await promise;
    this.envFetchPromises.delete(preset);
    return result;
  }

  /**
   * Assign the die's reflection cubemap, SLOT-AWARE:
   *
   * - **body** (and ring / faces / anything not a pip or image): ALWAYS gets the
   *   env map regardless of metalness/roughness — it's the large, visible surface
   *   and we always want its reflection.
   * - **pips**: get the env map when they actually use it — metalness > 0 (or a
   *   metalnessMap), a GLOSSY dielectric (roughness < 0.5: shiny plastic where the
   *   env light reads inside the pip), or a physical layer (glass/clearcoat/sheen/
   *   iridescence). Only a MATTE dielectric pip (metalness 0, rough, no physical)
   *   skips the cube — that's where it sampled for ~no visible gain at game scale.
   * - **image**: env map ALWAYS off — it's a flat logo/picture decal that gains
   *   nothing from reflections.
   *
   * Slot is resolved from the template `slotMap` (mesh name → slot), falling back
   * to name-based detection. envMapIntensity is reset fresh by applyMaterials each
   * acquire, so the per-die intensity multiply never compounds.
   */
  private applyEnvironment(
    mesh: THREE.Object3D,
    env: DiceEnvironment | undefined,
    slotMap?: Map<string, string[]>,
  ): void {
    const preset = env?.preset || '';
    const envTex = preset ? this.envCache.get(preset) ?? null : null;
    const intensityMul = env?.intensity ?? 1;

    // Reverse-map mesh name → slot. Clones preserve `name` (not `uuid`), so the
    // name keys are what match here; uuid keys simply never hit, which is fine.
    const nameToSlot = new Map<string, string>();
    if (slotMap) {
      for (const [slot, names] of slotMap) {
        for (const n of names) nameToSlot.set(n, slot);
      }
    }

    mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const meshNode = node as THREE.Mesh;
      const mats = Array.isArray(meshNode.material)
        ? (meshNode.material as THREE.Material[])
        : [meshNode.material as THREE.Material];

      for (const mat of mats) {
        const m = mat as any;
        // Only PBR materials carry envMapIntensity; shaders/basic skip cleanly.
        if (!('envMapIntensity' in m)) continue;

        // Body / ring / faces / unrecognized → always reflect. Pips reflect
        // unless matte dielectric. Image → never reflects.
        const slot =
          nameToSlot.get(meshNode.name) ||
          detectSlotName((m.name as string) || '') ||
          detectSlotName(meshNode.name) ||
          null;

        let usesEnv = !!envTex;
        if (slot === 'image') {
          // Flat decal — env always off.
          usesEnv = false;
        } else if (slot === 'pips') {
          // Keep env for metallic, glossy (roughness < 0.5 — env light reads
          // inside the pip), or physical-layer pips. Skip only matte dielectric.
          const metallic = (m.metalness ?? 0) > 0 || !!m.metalnessMap;
          const glossyDielectric = (m.roughness ?? 1) < 0.5;
          const physicalLayer =
            (m.transmission ?? 0) > 0 || (m.clearcoat ?? 0) > 0 ||
            (m.iridescence ?? 0) > 0 || (m.sheen ?? 0) > 0;
          usesEnv = !!envTex && (metallic || glossyDielectric || physicalLayer);
        }

        const prevEnvMap = m.envMap;
        if (usesEnv) {
          m.envMap = envTex;
          m.envMapIntensity = (m.envMapIntensity ?? 1) * intensityMul;
        } else {
          // Image, or a matte dielectric pip — clear any cube a pooled material
          // carried from a previous skin so it doesn't keep sampling it.
          m.envMap = null;
        }
        // Only the envMap's presence/identity keys the shader program;
        // envMapIntensity is a uniform. Re-flagging needsUpdate when nothing
        // changed forces a needless program re-derive per material per spawn.
        if (m.envMap !== prevEnvMap) m.needsUpdate = true;
      }
    });
  }

  /**
   * Force every cached env cube to re-upload to the GPU on the next render.
   * Under WKWebView memory pressure (the betting→shooting transition spike) the
   * GPU silently evicts the dice reflection cubes — there's NO webglcontextlost
   * event, so three.js never re-uploads them, and metallic surfaces render black
   * where the light hits. Re-flagging needsUpdate re-uploads them from the
   * retained source images once memory has settled after the transition. Cheap
   * (256² cubes). Call when entering a roll/shooting view.
   */
  refreshEnvironments(): void {
    for (const tex of this.envCache.values()) tex.needsUpdate = true;
  }

  // ── Query ───────────────────────────────────────────────────

  /** Check if a dice ID is fully loaded (document + GLB template) */
  isReady(diceId: string): boolean {
    const bundle = this.documentCache.get(diceId);
    if (!bundle) return false;
    return this.templateCache.has(bundle.model.code);
  }

  /**
   * Acquire the given skin, falling back to the starter red die when the slot
   * is empty (null) or its skin isn't loaded yet. The default is a REAL die
   * (correct face map + collider), unlike the procedural pip box, whose face
   * layout can contradict the authoritative roll result on screen. Returns
   * null only when even the default isn't ready (boot preloads it; a failed
   * fetch leaves the procedural box as the last resort).
   */
  acquireDiceOrDefault(diceId: string | null | undefined): DiceAsset | null {
    if (diceId && this.isReady(diceId)) return this.acquireDice(diceId);
    if (this.isReady(DEFAULT_DICE_ID)) return this.acquireDice(DEFAULT_DICE_ID);
    return null;
  }

  // ── Shader prewarm ──────────────────────────────────────────

  /**
   * Compile the shader programs for the given dice skins NOW, while a loading
   * screen is up, so the first roll never triggers a mid-game GLSL compile (a
   * GPU-memory spike that can lose the WebGL context on WKWebView — worse the
   * more dice/players are already resident). Acquires a throwaway die per ready
   * skin, compiles its materials against the live game lights, and releases it
   * back to the pool. Three dedupes programs by cache key, so this primes the
   * program for every later die of the same skin. Best-effort; unready skins
   * are skipped (they'll warm when their preload completes / on acquire).
   */
  async prewarmDiceShaders(
    diceIds: string[],
    camera: THREE.Camera,
    targetScene: THREE.Scene,
  ): Promise<void> {
    const unique = [...new Set(diceIds.filter(Boolean))];
    for (const id of unique) {
      if (!this.isReady(id)) continue;
      const asset = this.acquireDice(id);
      if (!asset) continue;
      try {
        await prewarmShaders(asset.mesh, camera, targetScene);
      } finally {
        this.releaseDice(asset);
      }
    }
  }

  // ── Acquire / Release ───────────────────────────────────────

  /**
   * Get a ready-to-use dice mesh. Returns null if not loaded.
   * Pool hit: reuse existing clone + re-apply materials.
   * Pool miss: clone from template + apply materials.
   */
  acquireDice(diceId: string): DiceAsset | null {
    const bundle = this.documentCache.get(diceId);
    if (!bundle) return null;

    const template = this.templateCache.get(bundle.model.code);
    if (!template) return null;

    // Physics box: prefer server-stored, fall back to computed from GLB
    const halfExtents = bundle.model.physicsBox?.halfExtents ?? template.halfExtents;
    const faceMap = bundle.model.faces;

    // Try pool first
    const poolKey = bundle.model.code;
    const poolArr = this.pool.get(poolKey);
    if (poolArr && poolArr.length > 0) {
      const asset = poolArr.pop()!;
      // Same skin coming back out of the pool (the common local/opponent
      // re-roll case): its materials + environment are already exactly right —
      // rebuilding them just allocates, configures, and disposes a full
      // material set per die for zero visual change. Only re-apply on an
      // actual skin change. (applyMaterials and applyEnvironment must be
      // skipped TOGETHER: applyEnvironment multiplies envMapIntensity onto
      // whatever applyMaterials just built.)
      if (asset.diceId !== diceId || asset.materialsMutated) {
        this.applyMaterials(asset.mesh, bundle.document.slots, template.slotMap);
        this.applyEnvironment(asset.mesh, bundle.document.environment, template.slotMap);
        asset.materialsMutated = false;
      }
      asset.diceId = diceId;
      asset.halfExtents = halfExtents;
      asset.faceMap = faceMap;
      asset.shaderMaterials = this.collectShaderMaterials(asset.mesh);
      for (const sm of asset.shaderMaterials) this.activeShaderMaterials.add(sm);
      asset.mesh.visible = true;
      renderScheduler.markShadowsDirty();
      return asset;
    }

    // Clone from template
    const mesh = template.scene.clone(true);

    // Clone materials so each instance is independent
    mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const m = node as THREE.Mesh;
      if (Array.isArray(m.material)) {
        m.material = m.material.map((mat) => mat.clone());
      } else {
        m.material = m.material.clone();
      }
    });

    // Apply DiceDocument materials + reflection environment
    this.applyMaterials(mesh, bundle.document.slots, template.slotMap);
    this.applyEnvironment(mesh, bundle.document.environment, template.slotMap);

    // Dice CAST shadows onto the table but do NOT receive them. Self-shadowing
    // a small faceted die (body, pip discs, etc.) produces shadow-acne speckle
    // on the bright pips — invisible on desktop's 24-bit shadow sampling, ugly
    // on the Android WebView's lower-precision sampling. receiveShadow=false on
    // every dice mesh part removes that self-shadow entirely; the cast shadow
    // onto the floor (the one that actually reads) is unaffected.
    mesh.traverse((node: THREE.Object3D) => {
      if ((node as THREE.Mesh).isMesh) {
        node.castShadow = true;
        node.receiveShadow = false;
      }
    });

    const shaderMaterials = this.collectShaderMaterials(mesh);
    for (const sm of shaderMaterials) this.activeShaderMaterials.add(sm);

    return {
      diceId,
      modelCode: bundle.model.code,
      mesh,
      halfExtents,
      faceMap,
      shaderMaterials,
      body: null,
    };
  }

  /** Return a dice to the pool for reuse */
  releaseDice(asset: DiceAsset): void {
    // Remove shader materials from active set
    for (const sm of asset.shaderMaterials) {
      this.activeShaderMaterials.delete(sm);
    }

    asset.mesh.visible = false;
    asset.mesh.parent?.remove(asset.mesh);
    // A caster left the scene — its shadow must disappear with it.
    renderScheduler.markShadowsDirty();

    const poolKey = asset.modelCode;
    if (!this.pool.has(poolKey)) this.pool.set(poolKey, []);
    const poolArr = this.pool.get(poolKey)!;

    // Duplicate guard + size limit
    if (poolArr.indexOf(asset) < 0 && poolArr.length < MAX_POOL_SIZE_PER_MODEL) {
      poolArr.push(asset);
    } else {
      // Pool full — dispose
      this.disposeAssetMeshes(asset);
    }
  }

  // ── Material Application ────────────────────────────────────

  /** Apply DiceDocument slot materials to a cloned mesh */
  private applyMaterials(
    mesh: THREE.Group,
    slots: Record<string, MaterialSlotConfig>,
    slotMap: Map<string, string[]>,
  ): void {
    for (const [slotName, config] of Object.entries(slots)) {
      const meshNames = slotMap.get(slotName);
      if (!meshNames) continue;

      const newMat = buildMaterial(config);

      mesh.traverse((node: THREE.Object3D) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const m = node as THREE.Mesh;
        const nameMatch = meshNames.includes(m.name) || meshNames.includes(m.uuid);

        // Also match by material name or slot detection
        if (!nameMatch) {
          const matName = (m.material as THREE.Material)?.name || '';
          const meshSlot = detectSlotName(matName) || detectSlotName(m.name);
          if (meshSlot !== slotName) return;
        }

        // Dispose old material and assign new
        if (Array.isArray(m.material)) {
          m.material.forEach((mat) => disposeMaterial(mat));
          m.material = [newMat.clone()];
        } else {
          disposeMaterial(m.material);
          m.material = newMat.clone();
        }
      });

      // Dispose the template material (clones were assigned)
      disposeMaterial(newMat);
    }
  }

  /** Collect all time-animated materials from a mesh tree: raw ShaderMaterials
   *  AND PBR shader presets (patched MeshPhysicalMaterials that expose their
   *  uniforms as mat.uniforms) — anything with a time uniform to drive. */
  private collectShaderMaterials(mesh: THREE.Object3D): THREE.ShaderMaterial[] {
    const result: THREE.ShaderMaterial[] = [];
    mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const mats = Array.isArray((node as THREE.Mesh).material)
        ? (node as THREE.Mesh).material as THREE.Material[]
        : [(node as THREE.Mesh).material as THREE.Material];
      for (const mat of mats) {
        if ((mat as THREE.ShaderMaterial).uniforms?.time) {
          result.push(mat as THREE.ShaderMaterial);
        }
      }
    });
    return result;
  }

  // ── Per-Frame Update ────────────────────────────────────────

  /** Update time uniform on all active shader materials */
  updateShaderTime(time: number): void {
    for (const sm of this.activeShaderMaterials) {
      sm.uniforms.time.value = time;
    }
  }

  /** True when any animated (time-driven) dice shader is live — local OR opponent
   *  dice share this manager, so one check covers both. Used by the render
   *  scheduler to keep drawing while a holographic/neon skin animates at rest. */
  hasActiveShaders(): boolean {
    return this.activeShaderMaterials.size > 0;
  }

  // ── Cleanup ─────────────────────────────────────────────────

  private disposeAssetMeshes(asset: DiceAsset): void {
    asset.mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const m = node as THREE.Mesh;
      if (Array.isArray(m.material)) {
        m.material.forEach((mat) => mat.dispose());
      } else {
        m.material.dispose();
      }
      // Don't dispose geometry — it's shared with the template
    });
  }

  /** Dispose all caches, templates, and pooled instances */
  dispose(): void {
    // Dispose pooled assets
    for (const poolArr of this.pool.values()) {
      for (const asset of poolArr) {
        this.disposeAssetMeshes(asset);
      }
    }
    this.pool.clear();

    // Dispose templates (geometry + materials)
    for (const template of this.templateCache.values()) {
      template.scene.traverse((node: THREE.Object3D) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const m = node as THREE.Mesh;
        m.geometry?.dispose();
        if (Array.isArray(m.material)) {
          m.material.forEach((mat) => mat.dispose());
        } else {
          m.material?.dispose();
        }
      });
    }
    this.templateCache.clear();

    // Dispose reflection env cube textures
    for (const tex of this.envCache.values()) tex.dispose();
    this.envCache.clear();
    this.envFetchPromises.clear();

    this.documentCache.clear();
    this.activeShaderMaterials.clear();
    this.glbFetchPromises.clear();
    this.apiFetchPromises.clear();

    clearTextureCache();
  }
}
